/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Row } from "react-bootstrap";
import { api } from "core/api";
import { Card, Button, SvgIcon } from "core/components";
import { ProjectFormData, ProjectStatus } from "core/schema";
import { material_returns_admin_project_path } from "routes";
import ProjectMaterialReturns from "./ProjectMaterialReturns";
import ProjectReturnForm from "./ProjectReturnForm";

type ProjectRequisitionsProps = {
  project: ProjectFormData;
};

const ProjectReturns = ({ project, states, sources }: ProjectRequisitionsProps): JSX.Element => {
  const [loadingMaterialReturns, setLoadingMaterialReturns] = React.useState(false);
  const [materialReturns, setMaterialReturns] = React.useState([]);
  const [showReturns, setShowReturns] = React.useState(true);
  const [source, setSource] = React.useState("");
  const policy = project.policy;

  const getMaterialReturns = async () => {
    try {
      setLoadingMaterialReturns(true);
      const response = await api.get(material_returns_admin_project_path(project.id));
      setMaterialReturns(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingMaterialReturns(false);
    }
  };

  React.useEffect(() => {
    if (showReturns) {
      getMaterialReturns().catch(console.error);
    }
  }, [showReturns]);

  const hasReturns = () => {
    return materialReturns.length > 0;
  };

  const onSubmit = () => {
    setShowReturns(!showReturns);
    setSource("");
  };

  const onCancel = () => {
    setShowReturns(!showReturns);
    setSource("");
  };

  const canShowReturns = () => {
    return [ProjectStatus.OnGoing, ProjectStatus.Completed].includes(project.status) && policy.can_save;
  };

  const noContent = () => {
    return (
      <Container fluid={true} className="text-center pt-lg-8 pb-lg-8">
        <Row className="mb-8">
          <Col>
            <SvgIcon name="clipboard-list" className="display-icon text-gray" />
          </Col>
        </Row>
        <Row>
          <Col>
            <h5>No returns yet.</h5>
          </Col>
        </Row>
      </Container>
    );
  };

  return (
    <Card.Body>
      <Container fluid={true}>
        <Row className="mb-2">
          <Col lg={12} className="d-flex justify-content-end">
            {showReturns && canShowReturns() && (
              <>
                <Button
                  className="button--brand mr-3"
                  icon="plus"
                  title="New Project Task Return"
                  type="button"
                  onClick={() => {
                    setShowReturns(!showReturns);
                    setSource("project_task");
                  }}
                />
                <Button
                  className="button--brand"
                  icon="plus"
                  title="New Change Order Return"
                  type="button"
                  onClick={() => {
                    setShowReturns(!showReturns);
                    setSource("change_order");
                  }}
                />
              </>
            )}
          </Col>
        </Row>
        <Row>
          {showReturns ? (
            <>
              {hasReturns() ? (
                <Col>
                  <ProjectMaterialReturns project={project} loading={loadingMaterialReturns} material_returns={materialReturns} sources={sources} />
                </Col>
              ) : (
                noContent()
              )}
            </>
          ) : (
            <ProjectReturnForm project={project} states={states} source={source} onCancel={onCancel} onSubmit={onSubmit} />
          )}
        </Row>
      </Container>
    </Card.Body>
  );
};

export default ProjectReturns;
